import { useEffect, useRef, useState, useCallback, useMemo, memo, forwardRef, useImperativeHandle } from 'react'
import * as pdfjsLib from 'pdfjs-dist'
import { TextLayer, setLayerDimensions } from 'pdfjs-dist'
import 'pdfjs-dist/web/pdf_viewer.css'
import {
  Download,
  Printer,
  Maximize2,
  Minimize2,
  Hand,
  Type,
  Search,
  X,
  ChevronUp,
  ChevronDown,
  Scan,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// Import PDF.js worker configuration
import '@/lib/pdfWorkerLoader'
import AdBlockerWarning from '@/components/AdBlockerWarning'
import { cn } from '@/lib/utils'
import { usePreventWheelPropagation } from '@/hooks/usePreventWheelPropagation'
import { apiService, type PageHighlights } from '@/service/api'
import {
  ZOOM_STEP_PERCENT,
  ZOOM_MIN_PERCENT,
  ZOOM_MAX_PERCENT_RASTER,
  clampPercent,
  snapPercentToStep,
} from '@/constants/zoom'
import { ZoomReadout } from '@/components/intueri/ZoomReadout'
import PreviewMatchList from '@/components/PreviewSearch/PreviewMatchList'
import type { PreviewMatch } from '@/components/PreviewSearch/types'

// Constants
const DEFAULT_ZOOM_ON_PDF = 0.5  // 50% zoom as default
const ZOOM_PERCENT_STEP = ZOOM_STEP_PERCENT // UI-28: consistent 1pp steps (e.g. 100%, 101%, 102%)
const ROTATION_DEGREES = 90
const FULL_ROTATION_DEGREES = 360
const PERCENTAGE_MULTIPLIER = 100
const MINIMUM_PAGE_NUMBER = 1
const INITIAL_ROTATION = 0
const PAGE_INCREMENT = 1
const PAGE_DECREMENT = 1
const DEFAULT_TOTAL_PAGES = 0
const MAX_PAGE_CACHE_SIZE = 10  // Limit cached PDF pages to prevent unbounded memory growth

// Helper function to extract file extension
const getFileExtension = (filename: string): string => {
  const ext = filename?.split('.').pop()
  return ext || 'file'
}

// Feature flags — set to true to re-enable when ready to ship
const ENABLE_TEXT_SELECTION = true
const ENABLE_DOCUMENT_SEARCH = true

const PDF_SEARCH_HIGHLIGHT_CLASS = 'pdf-search-highlight'

/** Remove all search highlight <mark> elements and restore plain text */
function clearSearchHighlights(root: HTMLElement | null): void {
  if (!root) return
  root.querySelectorAll(`.${PDF_SEARCH_HIGHLIGHT_CLASS}`).forEach((el) => {
    const parent = el.parentNode
    const text = document.createTextNode(el.textContent || '')
    parent?.replaceChild(text, el)
    // Merge adjacent text nodes so subsequent searches work on intact strings
    parent?.normalize()
  })
}

/**
 * Highlight all case-insensitive matches of query in the pdf.js text layer.
 *
 * pdf.js text layer spans use `color: transparent` + absolute positioning so
 * they sit invisibly on top of the canvas.  We wrap matched text in <mark>
 * elements that inherit `color: transparent` (keeping the text invisible) but
 * apply a semi-transparent background so the highlight is visible over the
 * rendered PDF beneath.
 */
function highlightSearchInElement(root: HTMLElement | null, query: string): number {
  if (!root || !query.trim()) return 0
  clearSearchHighlights(root)
  const q = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(q, 'gi')
  let count = 0
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  const textNodes: Text[] = []
  let n: Text | null
  while ((n = walker.nextNode() as Text | null)) {
    if (n.textContent && new RegExp(q, 'gi').test(n.textContent)) textNodes.push(n)
  }
  textNodes.forEach((node) => {
    const text = node.textContent || ''
    const indices: { start: number; end: number }[] = []
    let m: RegExpExecArray | null
    regex.lastIndex = 0
    while ((m = regex.exec(text)) !== null) {
      indices.push({ start: m.index, end: m.index + m[0].length })
      count++
    }
    if (indices.length === 0) return
    const fragment = document.createDocumentFragment()
    let lastEnd = 0
    indices.forEach(({ start, end }) => {
      if (start > lastEnd) {
        fragment.appendChild(document.createTextNode(text.slice(lastEnd, start)))
      }
      const mark = document.createElement('mark')
      mark.className = PDF_SEARCH_HIGHLIGHT_CLASS
      // Keep text invisible (inherit from .textLayer span) — only the
      // background is visible, sitting on top of the canvas-rendered text.
      mark.style.color = 'transparent'
      mark.style.backgroundColor = 'rgba(255, 235, 0, 0.6)'
      mark.style.borderRadius = '2px'
      mark.style.padding = '1px 0'
      mark.textContent = text.slice(start, end)
      fragment.appendChild(mark)
      lastEnd = end
    })
    if (lastEnd < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastEnd)))
    }
    node.parentNode?.replaceChild(fragment, node)
  })
  return count
}

type AutoFitMode = 'none' | 'width' | 'screen'

export interface PdfOutlineItem {
  title: string
  pageNumber: number
  level: number
}

interface PdfJsViewerProps {
  url: string | null
  currentPage: number
  onPageChange: (page: number) => void
  onTotalPagesChange: (total: number) => void
  initialRotation?: number
  initialZoom?: number
  initialAutoFit?: boolean
  documentName?: string
  documentLocation?: string
  documentId?: string
  /** When true, the built-in toolbar and search bar are hidden. Use the imperative ref to control zoom/rotate/etc. */
  hideToolbar?: boolean
  /** Called whenever the zoom percentage changes (0–500 range). */
  onZoomChange?: (zoomPercent: number) => void
  /** Called once after the PDF loads with the full document outline (bookmarks). Empty array if none. */
  onOutlineExtracted?: (items: PdfOutlineItem[]) => void
  /**
   * Open the viewer already searching for this text.
   *
   * Seeds the search box exactly as typing would, so everything downstream is
   * the existing behaviour: the debounced effect runs the search, jumps to the
   * nearest matching page and paints the highlights. A caller that knows WHICH
   * text the reader came for — a tag mention, say — can hand it over instead of
   * asking them to find it in a 40-page document.
   */
  initialSearch?: string
}

/** Imperative handle exposed via ref when hideToolbar is true. */
export interface PdfJsViewerHandle {
  zoomIn: () => void
  zoomOut: () => void
  /** Jump to a precise zoom percentage (FS-562 type-in). Snaps + clamps. */
  setZoomPercent: (percent: number) => void
  resetZoom: () => void
  rotateClockwise: () => void
  rotateCounterClockwise: () => void
  toggleFullscreen: () => void
  downloadPdf: () => void
  printPdf: () => void
  getZoomPercent: () => number
  toggleSearch: () => void
  toggleTextMode: () => void
  isTextMode: () => boolean
}

export const PdfJsViewer = memo(forwardRef<PdfJsViewerHandle, PdfJsViewerProps>(({
  url,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  initialRotation = INITIAL_ROTATION,
  initialZoom = DEFAULT_ZOOM_ON_PDF,
  initialAutoFit = true,
  documentName,
  documentLocation,
  documentId,
  hideToolbar = false,
  onZoomChange,
  onOutlineExtracted,
  initialSearch,
}, ref) => {
  const { t } = useTranslation('board')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null)
  const [scale, setScale] = useState(initialZoom)
  const [autoFitMode, setAutoFitMode] = useState<AutoFitMode>(initialAutoFit ? 'width' : 'none')
  const [containerWidth, setContainerWidth] = useState<number>(0)
  const [containerHeight, setContainerHeight] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const [rotation, setRotation] = useState(initialRotation)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [pageInputValue, setPageInputValue] = useState(currentPage.toString())
  const [hasCompatibilityIssues, setHasCompatibilityIssues] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  // Canvas dimensions captured AFTER each render completes — used by the
  // highlight overlay so it never reads stale values from the DOM ref.
  const [renderedCanvasDims, setRenderedCanvasDims] = useState<{ w: number; h: number } | null>(null)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  // Cross-page search state: pages with matches (0-indexed page numbers)
  const [matchingPages, setMatchingPages] = useState<number[]>([])
  const [totalMatchCount, setTotalMatchCount] = useState(0)
  // Setter is used to track per-page match counts; the value is currently
  // unread (UI surfaces totalMatchCount instead).
  const [_currentPageMatchCount, setCurrentPageMatchCount] = useState(0)
  // Position-based highlights from backend (bounding boxes + PyMuPDF page dimensions)
  const [positionHighlights, setPositionHighlights] = useState<Map<number, PageHighlights>>(new Map())
  // Per-page match counts from the API (used to compute the global match index)
  const [matchCountByPage, setMatchCountByPage] = useState<Map<number, number>>(new Map())
  // Page-level snippets from the API. FS-657 labels each row of the match list
  // with its own snippet; these cover pages with no word-position data, where
  // the API only gives one snippet for the whole page.
  const [snippetByPage, setSnippetByPage] = useState<Map<number, string>>(new Map())
  // Track which highlight is currently active for navigation (per-page, 0-based)
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(0)
  // Whether a search API call is in flight
  const [isSearching, setIsSearching] = useState(false)
  // For text-layer fallback: ref to signal "land on last match" to the timeout
  // callback (survives React strict mode cleanup/re-fire unlike reading in effect body).
  const pendingHighlightIndexRef = useRef<number | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null)
  const canvasContainerRef = useRef<HTMLDivElement | null>(null)
  const textLayerRef = useRef<HTMLDivElement>(null)
  const textLayerInstanceRef = useRef<TextLayer | null>(null)
  const renderGenRef = useRef(0)

  // Interaction mode: 'pan' for drag-to-pan, 'text' for text selection
  const [interactionMode, setInteractionMode] = useState<'pan' | 'text'>('pan')

  // Baseline scale: the fit-to-width scale so that 100% = full container width.
  // Must be state (not a ref) so React batches it with scale updates — a ref
  // would update inside the effect but the JSX would read stale values until
  // the next render, causing wrong zoom percentages in production.
  const [baseScale, setBaseScale] = useState<number>(DEFAULT_ZOOM_ON_PDF)

  // Drag-to-pan state
  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null)

  // Keyboard navigation throttle
  const lastKeyPressRef = useRef<number>(0)

  // Refs for props so async callbacks (e.g. debounced search) read the
  // latest values without needing them in their dependency arrays.
  const currentPageRef = useRef(currentPage)
  currentPageRef.current = currentPage
  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange

  // Navigation functions for toolbar buttons
  const handlePrevPage = useCallback(() => {
    if (currentPage <= MINIMUM_PAGE_NUMBER) return
    onPageChange(currentPage - PAGE_DECREMENT)
  }, [currentPage, onPageChange])

  const handleNextPage = useCallback(() => {
    if (!pdfDoc || currentPage >= pdfDoc.numPages) return
    onPageChange(currentPage + PAGE_INCREMENT)
  }, [pdfDoc, currentPage, onPageChange])

  // Global match index across the whole document (1-based), for "X of Y" display.
  const globalMatchIndex = useMemo(() => {
    if (totalMatchCount === 0) return 0
    const currentPage0 = currentPage - 1
    if (!matchCountByPage.has(currentPage0)) return 0
    let countBefore = 0
    for (const p of matchingPages) {
      if (p >= currentPage0) break
      countBefore += matchCountByPage.get(p) ?? 0
    }
    // Use activeHighlightIndex for position-based pages, currentMatchIndex for text-layer
    const pageData = positionHighlights.get(currentPage0)
    const indexOnPage = pageData && pageData.matches.length > 0
      ? activeHighlightIndex
      : currentMatchIndex
    return countBefore + indexOnPage + 1
  }, [currentPage, matchingPages, matchCountByPage, positionHighlights, activeHighlightIndex, currentMatchIndex, totalMatchCount])

  // Track whether positionHighlights changed (new search vs page navigation).
  // The highlight effect uses this to only reset activeHighlightIndex for new
  // search results, NOT when navigating between pages.
  const prevPositionHighlightsRef = useRef(positionHighlights)

  // FS-657: every match in the document, in the same order globalMatchIndex
  // counts them, so the list and the "X of Y" readout always agree.
  const allMatches = useMemo<PreviewMatch[]>(() => {
    if (!searchQuery.trim()) return []
    const out: PreviewMatch[] = []
    for (const page of matchingPages) {
      const locationLabel = t('preview_search.page_label', { page: page + 1 })
      const pageSnippet = snippetByPage.get(page) ?? searchQuery
      const pageData = positionHighlights.get(page)
      if (pageData && pageData.matches.length > 0) {
        pageData.matches.forEach((match, i) => {
          // Falls back while the Python tier predates per-match snippets (they're
          // computed at search time, so no re-index is involved).
          out.push({ id: `${page}-${i}`, locationLabel, snippet: match.snippet ?? pageSnippet })
        })
      } else {
        // Same default as globalMatchIndex and handleSelectMatch use, so a page
        // that somehow has no count contributes no phantom row.
        const count = matchCountByPage.get(page) ?? 0
        for (let i = 0; i < count; i++) {
          out.push({ id: `${page}-${i}`, locationLabel, snippet: pageSnippet })
        }
      }
    }
    return out
  }, [searchQuery, matchingPages, positionHighlights, matchCountByPage, snippetByPage, t])

  // Jump the viewer to a row the user clicked in the match list.
  const handleSelectMatch = useCallback((index: number) => {
    let remaining = index
    for (const page of matchingPages) {
      const count = matchCountByPage.get(page) ?? 0
      if (remaining >= count) {
        remaining -= count
        continue
      }
      setActiveHighlightIndex(remaining)
      if (page + 1 !== currentPage) {
        // The highlight effect lands on this index once the page has rendered.
        pendingHighlightIndexRef.current = remaining
        onPageChange(page + 1)
      } else if ((positionHighlights.get(page)?.matches.length ?? 0) === 0) {
        // Text-layer fallback on the page already shown: the effect won't
        // re-run, so move to the mark here.
        setCurrentMatchIndex(remaining)
        const marks = textLayerRef.current?.querySelectorAll(`.${PDF_SEARCH_HIGHLIGHT_CLASS}`)
        marks?.[remaining]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }
      return
    }
  }, [matchingPages, matchCountByPage, currentPage, onPageChange, positionHighlights])

  // Memoize options to prevent useEffect re-runs in the hook
  const isFirstPage = currentPage <= MINIMUM_PAGE_NUMBER
  const isLastPage = !!pdfDoc && currentPage >= pdfDoc.numPages
  const wheelOptions = useMemo(() => ({
    onScrollToBottom: handleNextPage,
    onScrollToTop: handlePrevPage,
    isLastPage,
    isFirstPage,
  }), [handleNextPage, handlePrevPage, isLastPage, isFirstPage])

  // Prevent wheel events from propagating to tldraw canvas
  // and enable scroll-to-navigate for page changes
  const wheelPreventionRef = usePreventWheelPropagation(wheelOptions)

  // Merge refs: canvasContainerRef for measurements, wheelPreventionRef for wheel handling
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    canvasContainerRef.current = node
    wheelPreventionRef(node)
  }, [wheelPreventionRef])

  // Cache for PDF page proxies to speed up page switching
  const pagesCacheRef = useRef<Map<number, pdfjsLib.PDFPageProxy>>(new Map())

  // Load PDF document
  useEffect(() => {
    // Clean up cached pages before clearing to prevent memory leaks
    pagesCacheRef.current.forEach(page => {
      try {
        page.cleanup()
      } catch {
        // Ignore cleanup errors
      }
    })
    pagesCacheRef.current.clear()

    if (!url) {
      setPdfDoc(null)
      return
    }

    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null
    let destroyed = false
    let currentPdf: pdfjsLib.PDFDocumentProxy | null = null

    const loadPdf = async () => {
      try {
        // Ensure URL is valid
        const pdfUrl = url.startsWith('blob:')
          ? url
          : new URL(url, window.location.href).href

        loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          // Disable worker termination on errors for better stability
          stopAtErrors: false,
          // Disable range requests for blob URLs for better compatibility
          disableRange: url.startsWith('blob:'),
          disableStream: url.startsWith('blob:'),
        })

        const pdf = await loadingTask.promise
        currentPdf = pdf

        if (!destroyed) {
          setPdfDoc(pdf)
          onTotalPagesChange(pdf.numPages)
          setError(null)

          // Extract outline (bookmarks) for TOC and pass to parent
          if (onOutlineExtracted) {
            pdf.getOutline().then(async (rawOutline) => {
              if (destroyed || !rawOutline || rawOutline.length === 0) {
                onOutlineExtracted([])
                return
              }
              const items: PdfOutlineItem[] = []
              const walk = async (entries: typeof rawOutline, level: number) => {
                for (const entry of entries) {
                  let pageNumber = 1
                  try {
                    if (entry.dest) {
                      const dest = typeof entry.dest === 'string'
                        ? await pdf.getDestination(entry.dest)
                        : entry.dest
                      if (dest?.[0]) pageNumber = (await pdf.getPageIndex(dest[0])) + 1
                    }
                  } catch { /* keep page 1 */ }
                  items.push({ title: entry.title, pageNumber, level })
                  if (entry.items?.length) await walk(entry.items, level + 1)
                }
              }
              await walk(rawOutline, 0)
              if (!destroyed) onOutlineExtracted(items)
            }).catch(() => onOutlineExtracted([]))
          }
        } else {
          // If component was destroyed while loading, clean up the PDF
          try {
            await pdf.destroy()
          } catch (error) {
            console.warn(
              'Error destroying PDF after component destruction:',
              error
            )
          }
        }
      } catch (reason) {
        if (!destroyed) {
          const fileType = documentName?.includes('.') ? getFileExtension(documentName) : 'document'
          setError(`${t('pdf_viewer.status.error_loading_file', { fileType })}: ${reason}`)
          console.error('Error loading PDF:', reason)
        }
      }
    }

    loadPdf()

    return () => {
      destroyed = true
      // Clean up cached pages to prevent memory leaks
      pagesCacheRef.current.forEach(page => {
        try {
          page.cleanup()
        } catch {
          // Ignore cleanup errors
        }
      })
      pagesCacheRef.current.clear()
      // Clean up loading task if still in progress
      if (loadingTask) {
        loadingTask.destroy().catch((error) => {
          console.warn('Error destroying PDF loading task:', error)
        })
      }
      // Clean up current PDF if it was loaded
      if (currentPdf) {
        currentPdf.destroy().catch((error) => {
          console.warn('Error destroying current PDF document:', error)
        })
      }
    }
  }, [url, onTotalPagesChange, t])

  // Cleanup render task and text layer on unmount to prevent memory leaks
  // This runs only once on unmount and cancels any in-progress render
  useEffect(() => {
    // Store refs for cleanup (avoids stale closure issues)
    const renderTask = renderTaskRef
    const textLayerInstance = textLayerInstanceRef
    return () => {
      // Cancel any in-progress render task to prevent memory leaks
      if (renderTask.current) {
        renderTask.current.cancel()
        renderTask.current = null
      }
      // Cancel any in-progress text layer render
      if (textLayerInstance.current) {
        try {
          textLayerInstance.current.cancel()
        } catch {
          // Ignore cancellation errors during cleanup
        }
        textLayerInstance.current = null
      }
    }
  }, [])

  // Update page input when currentPage changes
  useEffect(() => {
    setPageInputValue(currentPage.toString())
  }, [currentPage])

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  // Handle container resize to recalculate auto-fit scale
  useEffect(() => {
    if (!canvasContainerRef.current) return

    let resizeTimeout: ReturnType<typeof setTimeout> | null = null

    const resizeObserver = new ResizeObserver(() => {
      // Debounce resize updates to prevent lag when zooming with multiple documents
      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }
      resizeTimeout = setTimeout(() => {
        if (canvasContainerRef.current) {
          setContainerWidth(canvasContainerRef.current.clientWidth)
          setContainerHeight(canvasContainerRef.current.clientHeight)
        }
      }, 100)
    })

    resizeObserver.observe(canvasContainerRef.current)

    // Set initial dimensions
    if (canvasContainerRef.current) {
      setContainerWidth(canvasContainerRef.current.clientWidth)
      setContainerHeight(canvasContainerRef.current.clientHeight)
    }

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout)
      }
      resizeObserver.disconnect()
    }
  }, [])

  // Calculate scale to fit page width within container, capped at 100%
  const calculateFitToWidthScale = (pageWidth: number, containerWidth: number): number => {
    const padding = 40 // 20px on each side
    const availableWidth = containerWidth - padding
    // Guard against negative scale when container is too small
    if (availableWidth <= 0 || pageWidth <= 0) {
      return DEFAULT_ZOOM_ON_PDF // Fall back to default scale
    }
    return Math.min(availableWidth / pageWidth, 1)
  }

  // Calculate scale to fit entire page within container (both width and height)
  const calculateFitToScreenScale = (
    pageWidth: number, pageHeight: number,
    cWidth: number, cHeight: number
  ): number => {
    const padding = 40
    const availW = cWidth - padding
    const availH = cHeight - padding
    if (availW <= 0 || availH <= 0 || pageWidth <= 0 || pageHeight <= 0) {
      return DEFAULT_ZOOM_ON_PDF
    }
    return Math.min(availW / pageWidth, availH / pageHeight, 1)
  }

  // Render when page changes - simplified without queueing.
  // renderGenRef is incremented each time to detect stale async continuations.
  useEffect(() => {
    const renderCurrentPage = async () => {
      if (
        !pdfDoc ||
        !canvasRef.current ||
        currentPage < MINIMUM_PAGE_NUMBER ||
        currentPage > pdfDoc.numPages
      ) {
        return
      }

      // Cancel any ongoing render task before starting a new one
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel()
        } catch {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null
      }

      // Generation counter: if the effect re-runs (e.g., rapid page changes),
      // earlier async continuations detect they are stale and bail out before
      // touching the canvas.  This prevents "Cannot use the same canvas during
      // multiple render() operations" when scrolling quickly.
      const thisGen = ++renderGenRef.current

      // Start fade-out for smooth transition
      setIsRendering(true)

      try {
        // Use cached page if available, otherwise fetch and cache
        let page = pagesCacheRef.current.get(currentPage)
        if (page) {
          // Cache hit: move to end of Map for true LRU (most recently used)
          pagesCacheRef.current.delete(currentPage)
          pagesCacheRef.current.set(currentPage, page)
        } else {
          page = await pdfDoc.getPage(currentPage)

          // Bail out if a newer render started while we were waiting
          if (thisGen !== renderGenRef.current) return

          // LRU eviction: if cache is full, remove oldest (least recently used) entry
          if (pagesCacheRef.current.size >= MAX_PAGE_CACHE_SIZE) {
            const oldestKey = pagesCacheRef.current.keys().next().value
            if (oldestKey !== undefined) {
              const oldestPage = pagesCacheRef.current.get(oldestKey)
              try {
                oldestPage?.cleanup()
              } catch {
                // Ignore cleanup errors
              }
              pagesCacheRef.current.delete(oldestKey)
            }
          }
          pagesCacheRef.current.set(currentPage, page)
        }

        // Calculate auto-fit scale if container is available
        let renderScale = scale
        const effectiveContainerWidth = containerWidth || canvasContainerRef.current?.clientWidth || 0
        const baseViewport = page.getViewport({ scale: 1.0, rotation })

        // Always update the baseline scale (fit-to-width) so zoom % is relative to container width
        const fitToWidthScale = (effectiveContainerWidth > 0 && baseViewport.width > 0)
          ? calculateFitToWidthScale(baseViewport.width, effectiveContainerWidth)
          : undefined

        if (fitToWidthScale !== undefined) {
          setBaseScale(fitToWidthScale)
        }

        if (autoFitMode === 'width' && fitToWidthScale !== undefined) {
          renderScale = fitToWidthScale
          // Update scale state so zoom percentage displays correctly
          setScale(renderScale)
        } else if (autoFitMode === 'screen' && effectiveContainerWidth > 0) {
          const effectiveContainerHeight = containerHeight || canvasContainerRef.current?.clientHeight || 0
          if (effectiveContainerHeight > 0) {
            const fitScale = calculateFitToScreenScale(
              baseViewport.width, baseViewport.height,
              effectiveContainerWidth, effectiveContainerHeight
            )
            renderScale = fitScale
            setScale(fitScale)
          }
        }

        const viewport = page.getViewport({ scale: renderScale, rotation })
        const canvas = canvasRef.current
        if (!canvas || thisGen !== renderGenRef.current) return
        const context = canvas.getContext('2d')!

        // Clear and resize canvas
        canvas.height = viewport.height
        canvas.width = viewport.width
        context.clearRect(0, 0, canvas.width, canvas.height)

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
          canvas: canvas,
          // Prefer display intent so optional content / layer visibility matches on-screen PDF viewers
          intent: 'display' as const,
        }

        const renderTask = page.render(renderContext)
        renderTaskRef.current = renderTask
        await renderTask.promise
        renderTaskRef.current = null

        // Render text layer for text selection support
        if (textLayerRef.current) {
          try {
            // Cancel/clear previous text layer
            if (textLayerInstanceRef.current) {
              textLayerInstanceRef.current.cancel()
              textLayerInstanceRef.current = null
            }
            textLayerRef.current.innerHTML = ''

            const textContent = await page.getTextContent()
            if (thisGen !== renderGenRef.current) return // Page changed, abort stale render

            const textLayerDiv = textLayerRef.current
            if (!textLayerDiv) return
            // PDF.js setLayerDimensions sizes the text layer using CSS
            // variables normally defined on .pdfViewer .page.  Since we don't
            // use the standard PDF.js viewer chrome, we must set them ourselves.
            // Without these, the text layer is sized for scale=1 and misaligns
            // with the canvas — especially in smaller containers like the board preview.
            textLayerDiv.style.setProperty('--scale-factor', `${renderScale}`)
            textLayerDiv.style.setProperty('--user-unit', '1')
            textLayerDiv.style.setProperty('--total-scale-factor', `${renderScale}`)
            textLayerDiv.style.setProperty('--scale-round-x', '1px')
            textLayerDiv.style.setProperty('--scale-round-y', '1px')
            setLayerDimensions(textLayerDiv, viewport)

            const textLayer = new TextLayer({
              textContentSource: textContent,
              container: textLayerDiv,
              viewport: viewport,
            })
            textLayerInstanceRef.current = textLayer
            await textLayer.render()
            if (thisGen !== renderGenRef.current) {
              textLayer.cancel()
              return
            }

            // Add endOfContent div — pdfjs uses this to constrain drag-selection
            // so the highlight stays within the document and follows line boundaries
            const endOfContent = document.createElement('div')
            endOfContent.className = 'endOfContent'
            textLayerDiv.appendChild(endOfContent)
          } catch (textLayerError: unknown) {
            // Text layer rendering is non-critical — don't let it crash the viewer
            // Suppress cancellation errors during rapid page navigation
            if ((textLayerError as { name?: string })?.name !== 'RenderingCancelledException') {
              console.warn('Text layer render failed:', textLayerError)
            }
          }
        }

        // Fade back in after render completes and capture final canvas dims
        setRenderedCanvasDims({
          w: canvas.width,
          h: canvas.height,
        })
        setIsRendering(false)

        // Note: We don't call page.cleanup() here since we're caching pages for faster re-access
      } catch (error: unknown) {
        // Reset rendering state
        setIsRendering(false)

        // Filter out expected cancellation errors
        if ((error as { name?: string })?.name === 'RenderingCancelledException') {
          // This is expected during rapid navigation, not a real error
          return
        }
        // Filter out worker null errors that occur during rapid navigation
        // This happens when the worker message channel is null after canceling a render
        if (
          error instanceof TypeError &&
          error.message?.includes('sendWithPromise')
        ) {
          // This is expected during rapid navigation when worker is in transition state
          return
        }
        // All real errors still logged and displayed
        console.error('Error rendering page:', error)
        setError(`${t('pdf_viewer.status.error_rendering_page')}: ${error}`)
      }
    }

    renderCurrentPage()

    // Cleanup function — cancel render + text layer on page/zoom/rotation change
    return () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel()
        } catch {
          // Ignore cancellation errors during cleanup
        }
        renderTaskRef.current = null
      }
      if (textLayerInstanceRef.current) {
        try {
          textLayerInstanceRef.current.cancel()
        } catch {
          // Ignore cancellation errors during cleanup
        }
        textLayerInstanceRef.current = null
      }
    }
  }, [pdfDoc, currentPage, scale, rotation, containerWidth, containerHeight, autoFitMode])

  // Navigation functions (use the memoized callbacks)
  const onPrevPage = handlePrevPage
  const onNextPage = handleNextPage

  // Zoom functions
  const zoomIn = useCallback(() => {
    setAutoFitMode('none') // Disable auto-fit when user manually zooms
    setScale((s) => {
      const currentPercent = Math.round((s / baseScale) * PERCENTAGE_MULTIPLIER / ZOOM_PERCENT_STEP) * ZOOM_PERCENT_STEP
      const nextPercent = Math.min(currentPercent + ZOOM_PERCENT_STEP, ZOOM_MAX_PERCENT_RASTER)
      return (nextPercent / PERCENTAGE_MULTIPLIER) * baseScale
    })
  }, [baseScale])

  const zoomOut = useCallback(() => {
    setAutoFitMode('none') // Disable auto-fit when user manually zooms
    setScale((s) => {
      const currentPercent = Math.round((s / baseScale) * PERCENTAGE_MULTIPLIER / ZOOM_PERCENT_STEP) * ZOOM_PERCENT_STEP
      const nextPercent = Math.max(currentPercent - ZOOM_PERCENT_STEP, ZOOM_MIN_PERCENT)
      return (nextPercent / PERCENTAGE_MULTIPLIER) * baseScale
    })
  }, [baseScale])

  // FS-562 type-in: jump to a precise level. Snaps to the 10% grid and clamps
  // to the raster ceiling so a typed 9999 lands at 400%, not off the rails.
  const setZoomPercent = useCallback((percent: number) => {
    setAutoFitMode('none')
    const target = clampPercent(snapPercentToStep(percent), ZOOM_MIN_PERCENT, ZOOM_MAX_PERCENT_RASTER)
    setScale((target / PERCENTAGE_MULTIPLIER) * baseScale)
  }, [baseScale])

  const resetZoom = useCallback(() => {
    setAutoFitMode('width') // Re-enable fit-to-width when user resets zoom
    setScale(DEFAULT_ZOOM_ON_PDF)
  }, [])

  const fitToScreen = useCallback(() => {
    setAutoFitMode('screen')
  }, [])

  // Expose zoom percentage and notify parent of changes
  const getZoomPercent = useCallback(() => {
    return Math.round((scale / baseScale) * PERCENTAGE_MULTIPLIER)
  }, [scale, baseScale])

  useEffect(() => {
    onZoomChange?.(getZoomPercent())
  }, [scale, baseScale, onZoomChange, getZoomPercent])

  // Expose imperative handle for external toolbar control
  useImperativeHandle(ref, () => ({
    zoomIn,
    zoomOut,
    setZoomPercent,
    resetZoom,
    rotateClockwise,
    rotateCounterClockwise,
    toggleFullscreen,
    downloadPdf,
    printPdf,
    getZoomPercent,
    toggleSearch: () => {
      setIsSearchOpen(prev => {
        if (!prev) {
          setTimeout(() => searchInputRef.current?.focus(), 0)
        }
        return !prev
      })
    },
    toggleTextMode: () => {
      setInteractionMode(prev => prev === 'text' ? 'pan' : 'text')
    },
    isTextMode: () => interactionMode === 'text',
  }))

  // Ctrl+Scroll to zoom inside document
  useEffect(() => {
    const container = canvasContainerRef.current
    if (!container) return

    const handleCtrlWheel = (e: WheelEvent) => {
      // Only handle Ctrl+scroll for zoom
      if (!e.ctrlKey && !e.metaKey) return

      e.preventDefault()
      e.stopPropagation()

      if (e.deltaY < 0) {
        zoomIn()
      } else {
        zoomOut()
      }
    }

    container.addEventListener('wheel', handleCtrlWheel, { passive: false })

    return () => {
      container.removeEventListener('wheel', handleCtrlWheel)
    }
  }, [zoomIn, zoomOut])

  // Drag-to-pan handlers for content area
  const handleDragStart = useCallback((e: React.MouseEvent | React.PointerEvent) => {
    const container = canvasContainerRef.current
    if (!container) return

    // Only start drag with left mouse button
    if (e.button !== 0) return

    // In text mode, don't intercept pointer events (let text layer handle selection)
    if (interactionMode === 'text') {
      e.stopPropagation()
      container.focus()
      return
    }

    e.stopPropagation()
    e.preventDefault()

    // Manually focus container for keyboard navigation (preventDefault blocks auto-focus)
    container.focus()

    setIsDragging(true)
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
    }
  }, [interactionMode])

  const handleDragMove = useCallback((e: React.MouseEvent | React.PointerEvent) => {
    if (!isDragging || !dragStartRef.current) return
    
    const container = canvasContainerRef.current
    if (!container) return

    e.stopPropagation()
    e.preventDefault()

    const dx = e.clientX - dragStartRef.current.x
    const dy = e.clientY - dragStartRef.current.y

    container.scrollLeft = dragStartRef.current.scrollLeft - dx
    container.scrollTop = dragStartRef.current.scrollTop - dy
  }, [isDragging])

  const handleDragEnd = useCallback((e: React.MouseEvent | React.PointerEvent) => {
    if (isDragging) {
      e.stopPropagation()
      e.preventDefault()
    }
    setIsDragging(false)
    dragStartRef.current = null
  }, [isDragging])

  // Global mouse up listener to handle drag end outside the container
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false)
      dragStartRef.current = null
    }

    if (isDragging) {
      window.addEventListener('mouseup', handleGlobalMouseUp)
      window.addEventListener('pointerup', handleGlobalMouseUp)
    }

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp)
      window.removeEventListener('pointerup', handleGlobalMouseUp)
    }
  }, [isDragging])

  // Keyboard navigation for Page Up/Down
  // Listen at window level and check if viewer is active (more reliable than container focus)
  useEffect(() => {
    const THROTTLE_MS = 150
    const SCROLL_BOUNDARY_THRESHOLD = 5

    const handleKeyDown = (e: KeyboardEvent) => {
      // Throttle rapid key presses to prevent scroll conflicts
      const now = Date.now()
      if (now - lastKeyPressRef.current < THROTTLE_MS) return

      const scrollContainer = canvasContainerRef.current
      const mainContainer = containerRef.current
      if (!scrollContainer || !mainContainer) return

      // Only handle if the viewer or its children have focus
      const activeElement = document.activeElement
      const isViewerActive = mainContainer.contains(activeElement) || mainContainer === activeElement

      if (!isViewerActive) return

      // Don't handle if user is typing in an input field
      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') return

      // Only handle Page Up/Down keys
      if (e.key !== 'PageDown' && e.key !== 'PageUp') return

      e.preventDefault()
      lastKeyPressRef.current = now

      const scrollAmount = scrollContainer.clientHeight * 0.9
      const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight

      if (e.key === 'PageDown') {
        const newScrollTop = Math.min(scrollContainer.scrollTop + scrollAmount, maxScrollTop)

        // Navigate to next page if scroll would reach bottom (but not on last page)
        if (newScrollTop >= maxScrollTop - SCROLL_BOUNDARY_THRESHOLD) {
          if (pdfDoc && currentPage < pdfDoc.numPages) {
            handleNextPage()
            // Reset scroll to top for new page
            scrollContainer.scrollTo({ top: 0, behavior: 'instant' })
          }
          // On last page, just scroll to bottom without resetting
        } else {
          scrollContainer.scrollTo({ top: newScrollTop, behavior: 'smooth' })
        }
      } else if (e.key === 'PageUp') {
        const newScrollTop = Math.max(scrollContainer.scrollTop - scrollAmount, 0)

        // Navigate to previous page if scroll would reach top (but not on first page)
        if (newScrollTop <= SCROLL_BOUNDARY_THRESHOLD) {
          if (currentPage > MINIMUM_PAGE_NUMBER) {
            handlePrevPage()
            // Reset scroll to bottom for previous page (to continue reading upward)
            scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'instant' })
          }
          // On first page, just scroll to top without resetting
        } else {
          scrollContainer.scrollTo({ top: newScrollTop, behavior: 'smooth' })
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleNextPage, handlePrevPage, pdfDoc, currentPage])

  // Keyboard shortcuts for interaction mode (H = pan, T = text)
  useEffect(() => {
    const mainContainer = containerRef.current
    if (!mainContainer) return

    const handleModeKeyDown = (e: KeyboardEvent) => {
      // Don't handle if user is typing in an input field
      const activeElement = document.activeElement
      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') return

      // Only handle if the viewer or its children have focus
      if (!mainContainer.contains(activeElement) && mainContainer !== activeElement) return

      if (e.key === 'h' || e.key === 'H') {
        setInteractionMode('pan')
      } else if (ENABLE_TEXT_SELECTION && (e.key === 't' || e.key === 'T')) {
        setInteractionMode('text')
      }
    }

    window.addEventListener('keydown', handleModeKeyDown)
    return () => window.removeEventListener('keydown', handleModeKeyDown)
  }, [])

  // Remove 'selecting' class on mouseup so the endOfContent div resets.
  // Listen on both window and document to catch events that tldraw may stop
  // from reaching window (e.g. inside an HTMLContainer on the board).
  useEffect(() => {
    if (interactionMode !== 'text') return

    const handleMouseUp = () => {
      textLayerRef.current?.classList.remove('selecting')
    }

    // Also listen on document: fires before window in propagation order, so it
    // still runs if tldraw calls stopImmediatePropagation() at its own
    // window-level handler.  The duplicate invocation is harmless —
    // classList.remove is a no-op when the class is already absent.
    document.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [interactionMode])

  // Intercept Ctrl+C / Cmd+C when in text mode so the selected text is copied
  // to the clipboard instead of being swallowed by tldraw's editor-level copy.
  useEffect(() => {
    if (interactionMode !== 'text') return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        const selection = window.getSelection()
        const selectedText = selection?.toString()
        if (selectedText) {
          e.stopPropagation()
          e.preventDefault()
          navigator.clipboard.writeText(selectedText).then(() => {
            toast.success(t('pdf_viewer.status.text_copied', 'Text copied to clipboard'))
          })
        }
      }
    }

    // Use capture phase so we run before tldraw's listeners
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [interactionMode, t])

  // Cmd+F / Ctrl+F: open document search and focus input
  useEffect(() => {
    if (!ENABLE_DOCUMENT_SEARCH) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        const target = e.target as HTMLElement
        const isTextInput =
          target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
          (target.isContentEditable && target.getAttribute('contenteditable') === 'true')
        if (isTextInput) return
        const mainContainer = containerRef.current
        if (!mainContainer?.contains(target) && mainContainer !== target) return
        e.preventDefault()
        setIsSearchOpen(true)
        setInteractionMode('pan')
        setTimeout(() => searchInputRef.current?.focus(), 0)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleFindNext = useCallback(() => {
    if (!searchQuery.trim()) return

    // Position-based navigation
    const pageData = positionHighlights.get(currentPage - 1)
    const pageMatches = pageData?.matches
    if (pageMatches && pageMatches.length > 0) {
      const next = activeHighlightIndex + 1
      if (next < pageMatches.length) {
        setActiveHighlightIndex(next)
        return
      }
      // At last match on this page — jump to next page with matches
      const currentPage0 = currentPage - 1
      const nextPage = matchingPages.find((p) => p > currentPage0)
      if (nextPage !== undefined) {
        setActiveHighlightIndex(0)
        pendingHighlightIndexRef.current = 0
        onPageChange(nextPage + 1)
      }
      return
    }

    // Fallback: text-layer mark navigation
    const layer = textLayerRef.current
    if (!layer) return
    const marks = layer.querySelectorAll(`.${PDF_SEARCH_HIGHLIGHT_CLASS}`)

    if (marks.length > 0) {
      setCurrentMatchIndex((i) => {
        const next = i + 1
        if (next < marks.length) {
          marks[next]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
          return next
        }
        const currentPage0 = currentPage - 1
        const nextPage = matchingPages.find((p) => p > currentPage0)
        if (nextPage !== undefined) {
          pendingHighlightIndexRef.current = 0
          onPageChange(nextPage + 1)
        }
        return i
      })
    } else if (matchingPages.length > 0) {
      const currentPage0 = currentPage - 1
      const nextPage = matchingPages.find((p) => p > currentPage0) ?? matchingPages[0]
      if (nextPage !== undefined) {
        pendingHighlightIndexRef.current = 0
        onPageChange(nextPage + 1)
      }
    }
  }, [searchQuery, currentPage, matchingPages, onPageChange, positionHighlights, activeHighlightIndex])

  const handleFindPrevious = useCallback(() => {
    if (!searchQuery.trim()) return

    // Position-based navigation
    const pageData = positionHighlights.get(currentPage - 1)
    const pageMatches = pageData?.matches
    if (pageMatches && pageMatches.length > 0) {
      const prev = activeHighlightIndex - 1
      if (prev >= 0) {
        setActiveHighlightIndex(prev)
        return
      }
      // At first match on this page — jump to previous page with matches
      const currentPage0 = currentPage - 1
      const prevPage = [...matchingPages].reverse().find((p) => p < currentPage0)
      if (prevPage !== undefined) {
        // Set to last match on target page.  For position-based pages, set
        // activeHighlightIndex directly.  Also set pendingHighlightIndexRef
        // so the text-layer fallback lands on the last mark if the target
        // page has no position data.
        const prevPageData = positionHighlights.get(prevPage)
        const lastIdx = Math.max(0, (prevPageData?.matches?.length ?? 1) - 1)
        setActiveHighlightIndex(lastIdx)
        pendingHighlightIndexRef.current = -1
        onPageChange(prevPage + 1)
      }
      return
    }

    // Fallback: text-layer mark navigation
    const layer = textLayerRef.current
    if (!layer) return
    const marks = layer.querySelectorAll(`.${PDF_SEARCH_HIGHLIGHT_CLASS}`)

    if (marks.length > 0) {
      setCurrentMatchIndex((i) => {
        const prev = i - 1
        if (prev >= 0) {
          marks[prev]?.scrollIntoView({ block: 'center', behavior: 'smooth' })
          return prev
        }
        const currentPage0 = currentPage - 1
        const prevPage = [...matchingPages].reverse().find((p) => p < currentPage0)
        if (prevPage !== undefined) {
          pendingHighlightIndexRef.current = -1
          onPageChange(prevPage + 1)
        }
        return i
      })
    } else if (matchingPages.length > 0) {
      const currentPage0 = currentPage - 1
      const prevPage = [...matchingPages].reverse().find((p) => p < currentPage0) ?? matchingPages[matchingPages.length - 1]
      if (prevPage !== undefined) {
        pendingHighlightIndexRef.current = -1
        onPageChange(prevPage + 1)
      }
    }
  }, [searchQuery, currentPage, matchingPages, onPageChange, positionHighlights, activeHighlightIndex])

  /**
   * Seed the search from `initialSearch`. Deliberately just sets the same two
   * pieces of state the search box sets — the debounced effect above owns
   * running the query, the page jump and the highlighting, so a seeded search
   * and a typed one cannot diverge.
   */
  useEffect(() => {
    const seed = initialSearch?.trim()
    if (!seed) return
    setIsSearchOpen(true)
    setSearchQuery(seed)
  }, [initialSearch])

  const handleCloseSearch = useCallback(() => {
    setIsSearchOpen(false)
    setSearchQuery('')
    setMatchingPages([])
    setTotalMatchCount(0)
    setCurrentPageMatchCount(0)
    setPositionHighlights(new Map())
    setMatchCountByPage(new Map())
    setSnippetByPage(new Map())
    setActiveHighlightIndex(0)
    setSearchResolvedQuery('')
    clearSearchHighlights(textLayerRef.current)
    // Abort any in-flight search API request so a late response
    // doesn't re-populate highlights after search is closed
    searchAbortRef.current?.abort()
    pendingHighlightIndexRef.current = null
  }, [])

  // Perform cross-page search via backend API.
  // searchResolvedQuery tracks which query the positionHighlights correspond to,
  // so the highlight effect can distinguish "API hasn't responded yet" from
  // "API responded with no position data" and avoid a text-layer flash.
  const searchAbortRef = useRef<AbortController | null>(null)
  const [searchResolvedQuery, setSearchResolvedQuery] = useState('')
  const executeSearch = useCallback(async (query: string): Promise<number[]> => {
    if (!documentId || !query.trim()) {
      setMatchingPages([])
      setTotalMatchCount(0)
      setPositionHighlights(new Map())
      setMatchCountByPage(new Map())
      setSnippetByPage(new Map())
      setSearchResolvedQuery('')
      return []
    }
    // Cancel any in-flight request
    searchAbortRef.current?.abort()
    const controller = new AbortController()
    searchAbortRef.current = controller
    setIsSearching(true)
    try {
      const response = await apiService.searchDocuments(query, [documentId])
      if (controller.signal.aborted) {
        // New request already owns isSearching; don't clear it here
        return []
      }
      const pages = response.results.map((r: { page: number }) => r.page).sort((a: number, b: number) => a - b)
      const uniquePages = [...new Set(pages)]
      setMatchingPages(uniquePages)

      // Collect position-based highlights per page from search results
      const hlMap = new Map<number, PageHighlights>()
      for (const result of response.results) {
        if (result.page_highlights && result.page_highlights.boxes.length > 0) {
          const existing = hlMap.get(result.page)
          if (existing) {
            existing.boxes.push(...result.page_highlights.boxes)
            existing.matches.push(...result.page_highlights.matches)
            existing.match_count += result.page_highlights.match_count
          } else {
            hlMap.set(result.page, { ...result.page_highlights })
          }
        }
      }
      setPositionHighlights(hlMap)

      // Build per-page match counts in navigable units (matches.length for
      // position-based pages, API result count for text-layer fallback pages).
      // This must match what handleFindNext/Previous actually step through.
      const countByPage = new Map<number, number>()
      const snippets = new Map<number, string>()
      for (const result of response.results) {
        countByPage.set(result.page, (countByPage.get(result.page) || 0) + 1)
        if (!snippets.has(result.page) && result.text) snippets.set(result.page, result.text)
      }
      setSnippetByPage(snippets)
      for (const [page, data] of hlMap) {
        countByPage.set(page, data.matches.length)
      }
      setMatchCountByPage(countByPage)

      // Total = sum of per-page navigable counts (consistent with global index)
      let total = 0
      for (const p of uniquePages) {
        total += countByPage.get(p) ?? 0
      }
      setTotalMatchCount(total)

      setSearchResolvedQuery(query)
      setIsSearching(false)
      return uniquePages
    } catch {
      if (!controller.signal.aborted) {
        setMatchingPages([])
        setTotalMatchCount(0)
        setPositionHighlights(new Map())
        setMatchCountByPage(new Map())
        setSnippetByPage(new Map())
        setSearchResolvedQuery(query)
        setIsSearching(false)
      }
      return []
    }
  }, [documentId])

  // Debounced search as user types (200ms).
  // searchDebounceRef lets Enter bypass the debounce without a duplicate call.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!searchQuery.trim()) {
      searchAbortRef.current?.abort()
      setIsSearching(false)
      setMatchingPages([])
      setTotalMatchCount(0)
      setPositionHighlights(new Map())
      setMatchCountByPage(new Map())
      setSnippetByPage(new Map())
      setSearchResolvedQuery('')
      return
    }
    setIsSearching(true)
    searchDebounceRef.current = setTimeout(async () => {
      const pages = await executeSearch(searchQuery)
      if (pages.length === 0) return
      // Auto-navigate to the nearest match (like Ctrl+F in a browser)
      const cp = currentPageRef.current - 1
      if (pages.includes(cp)) {
        setActiveHighlightIndex(0)
        return // current page already has matches
      }
      const nextMatch = pages.find((p) => p >= cp)
      setActiveHighlightIndex(0)
      onPageChangeRef.current((nextMatch !== undefined ? nextMatch : pages[0]) + 1)
    }, 200)
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current)
        setIsSearching(false)
      }
    }
  }, [searchQuery, executeSearch])

  // Apply or clear search highlights when query or page changes.
  // Position-based highlights take priority.  Text-layer highlighting only
  // runs as a fallback for documents indexed before the position feature,
  // and only AFTER the API has responded (searchResolvedQuery matches) to
  // avoid a flash where text-layer highlights appear then get replaced.
  useEffect(() => {
    const layer = textLayerRef.current
    if (!searchQuery.trim()) {
      if (layer) clearSearchHighlights(layer)
      setCurrentMatchIndex(0)
      setActiveHighlightIndex(0)
      setCurrentPageMatchCount(0)
      return
    }

    // Detect new search results vs page navigation — only reset
    // activeHighlightIndex for new results so Find Prev/Next survive page changes.
    const isNewSearch = positionHighlights !== prevPositionHighlightsRef.current
    prevPositionHighlightsRef.current = positionHighlights

    // Use position-based highlights when available (extracted during indexing)
    const pageData = positionHighlights.get(currentPage - 1)
    const pageHighlights = pageData?.boxes
    if (pageHighlights && pageHighlights.length > 0) {
      // This page is position-based, so the text-layer path won't consume the
      // pending index — drop it here or it leaks onto the next fallback page and
      // activates the wrong mark there.
      pendingHighlightIndexRef.current = null
      // Clear any text-layer marks so they don't overlap
      if (layer) clearSearchHighlights(layer)
      setCurrentPageMatchCount(pageData?.match_count ?? pageHighlights.length)
      if (isNewSearch) {
        setActiveHighlightIndex(0)
      }
      return
    }

    // Don't fall back to text-layer highlighting while the API call is
    // still pending — this prevents a flash where text-layer marks appear
    // briefly before being replaced by position-based highlights.
    if (searchResolvedQuery !== searchQuery) return

    // Fallback: text-layer highlighting for older documents without position data.
    // Read pendingHighlightIndexRef inside the timeout (not at effect top) so the
    // ref survives effect cleanup/re-fire in React strict mode.
    if (!layer) return
    const timer = setTimeout(() => {
      const count = highlightSearchInElement(textLayerRef.current, searchQuery)
      const pending = pendingHighlightIndexRef.current
      pendingHighlightIndexRef.current = null
      const idx = pending === -1 ? Math.max(0, count - 1) : (pending ?? 0)
      setCurrentMatchIndex(idx)
      setCurrentPageMatchCount(count)
      // Don't set activeHighlightIndex here — navigation functions and the
      // debounce auto-navigate already handle it before triggering the page change.
      if (count > 0) {
        const marks = textLayerRef.current?.querySelectorAll(`.${PDF_SEARCH_HIGHLIGHT_CLASS}`)
        marks?.[idx]?.scrollIntoView({ block: 'center', behavior: 'auto' })
      }
    }, 50)
    return () => clearTimeout(timer)
  }, [searchQuery, currentPage, positionHighlights, searchResolvedQuery])

  // Bring the active position-based match into view. Without this, picking a
  // match while zoomed in just recolours a box that's outside the viewport.
  // Text-layer matches scroll themselves via scrollIntoView on the <mark>.
  useEffect(() => {
    if (!isSearchOpen || isRendering || !renderedCanvasDims) return
    const container = canvasContainerRef.current
    const canvas = canvasRef.current
    const pageData = positionHighlights.get(currentPage - 1)
    const box = pageData?.matches[activeHighlightIndex]?.boxes[0]
    if (!container || !canvas || !box) return
    if (!pageData?.page_width || !pageData?.page_height) return

    const scaleX = renderedCanvasDims.w / pageData.page_width
    const scaleY = renderedCanvasDims.h / pageData.page_height
    const canvasRect = canvas.getBoundingClientRect()
    const viewRect = container.getBoundingClientRect()
    const top = canvasRect.top - viewRect.top + box.y0 * scaleY
    const left = canvasRect.left - viewRect.left + box.x0 * scaleX
    const height = Math.max(1, (box.y1 - box.y0) * scaleY)
    const width = Math.max(1, (box.x1 - box.x0) * scaleX)

    const MARGIN_PX = 24
    if (top < MARGIN_PX || top + height > container.clientHeight - MARGIN_PX) {
      container.scrollTop += top - (container.clientHeight - height) / 2
    }
    if (left < MARGIN_PX || left + width > container.clientWidth - MARGIN_PX) {
      container.scrollLeft += left - (container.clientWidth - width) / 2
    }
  }, [isSearchOpen, isRendering, renderedCanvasDims, positionHighlights, currentPage, activeHighlightIndex])

  // Download function
  const downloadPdf = () => {
    if (!url) return
    const link = document.createElement('a')
    link.href = url

    // Determine download filename
    let downloadName = 'document.pdf'
    if (documentName) {
      // If document was converted from Office format to PDF, adjust the extension
      if (documentLocation?.toLowerCase().endsWith('.pdf') && !documentName.toLowerCase().endsWith('.pdf')) {
        const nameWithoutExt = documentName.includes('.') ? documentName.substring(0, documentName.lastIndexOf('.')) : documentName
        downloadName = `${nameWithoutExt}.pdf`
      } else {
        downloadName = documentName
      }
    }

    link.download = downloadName
    link.click()
  }

  // Print function
  const printPdf = () => {
    if (!url) return
    const printWindow = window.open(url)
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print()
      })
    }
  }

  // Rotation functions
  const rotateClockwise = () => {
    setRotation((prev) => (prev + ROTATION_DEGREES) % FULL_ROTATION_DEGREES)
  }

  const rotateCounterClockwise = () => {
    setRotation(
      (prev) =>
        (prev - ROTATION_DEGREES + FULL_ROTATION_DEGREES) %
        FULL_ROTATION_DEGREES
    )
  }

  // Fullscreen toggle
  const toggleFullscreen = async () => {
    if (!containerRef.current) return

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen()
        setIsFullscreen(true)
      } else {
        await document.exitFullscreen()
        setIsFullscreen(false)
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
    }
  }

  // Handle page input
  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value)
  }

  const handlePageInputSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const pageNum = parseInt(pageInputValue, 10)
      if (
        !isNaN(pageNum) &&
        pageNum >= MINIMUM_PAGE_NUMBER &&
        pdfDoc &&
        pageNum <= pdfDoc.numPages
      ) {
        onPageChange(pageNum)
      } else {
        // Reset to current page if invalid
        setPageInputValue(currentPage.toString())
      }
    }
  }

  const createBoardSafeHandler = (handler: () => void) => {
    return (e: React.MouseEvent | React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      handler()
    }
  }

  const stopBoardEvents = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation()
  }


  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    )
  }

  if (!url) {
    if (documentName && documentName.includes('.')) {
      return (
        <div className="h-full flex items-center justify-center">
          <div className="text-gray-500 dark:text-dark-text-muted">{t('pdf_viewer.status.no_file_loaded', { fileType: getFileExtension(documentName) })}</div>
        </div>
      )
    }
    return null
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col">
      {/* Ad Blocker Detection */}
      <AdBlockerWarning
        onDetectionComplete={setHasCompatibilityIssues}
        showToast={true}
        showInline={hasCompatibilityIssues}
      />

      {/* Toolbar — hidden when parent provides its own */}
      {!hideToolbar && <div
        className="flex items-center justify-between p-2 border-b bg-gray-50 dark:bg-dark-muted flex-wrap gap-2"
        onPointerDown={stopBoardEvents}
        onClick={stopBoardEvents}
        onMouseDown={stopBoardEvents}
      >
        {/* Left side - Navigation and page input */}
        <div className="flex items-center gap-2">
          <button
            onClick={createBoardSafeHandler(onPrevPage)}
            onPointerDown={stopBoardEvents}
            disabled={currentPage <= MINIMUM_PAGE_NUMBER}
            className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('pdf_viewer.navigation.previous_page')}
          >
            ←
          </button>
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={pageInputValue}
              onChange={handlePageInputChange}
              onKeyDown={handlePageInputSubmit}
              onPointerDown={stopBoardEvents}
              onClick={stopBoardEvents}
              className="w-12 px-2 py-1 text-sm text-center border rounded bg-white dark:bg-dark-card"
              title={t('pdf_viewer.navigation.go_to_page')}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              / {pdfDoc?.numPages || DEFAULT_TOTAL_PAGES}
            </span>
          </div>
          <button
            onClick={createBoardSafeHandler(onNextPage)}
            onPointerDown={stopBoardEvents}
            disabled={!pdfDoc || currentPage >= pdfDoc.numPages}
            className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('pdf_viewer.navigation.next_page')}
          >
            →
          </button>
        </div>

        {/* Center - Zoom controls */}
        <TooltipProvider>
          <div className="flex items-center gap-2">
            <button
              onClick={createBoardSafeHandler(zoomOut)}
              onPointerDown={stopBoardEvents}
              className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
              title={t('pdf_viewer.zoom.zoom_out')}
            >
              -
            </button>
            <div onPointerDown={stopBoardEvents}>
              <ZoomReadout
                percent={Math.round((scale / baseScale) * PERCENTAGE_MULTIPLIER)}
                onSetPercent={setZoomPercent}
                label={t('common:zoom.level', { defaultValue: 'Zoom level' })}
                className="px-3 py-1 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent min-w-[60px] tabular-nums cursor-text"
                inputClassName="px-3 py-1 text-sm bg-white dark:bg-dark-card border rounded min-w-[60px] tabular-nums text-center outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={createBoardSafeHandler(zoomIn)}
              onPointerDown={stopBoardEvents}
              className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
              title={t('pdf_viewer.zoom.zoom_in')}
            >
              +
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={createBoardSafeHandler(fitToScreen)}
                  onPointerDown={stopBoardEvents}
                  className={cn(
                    'p-2 text-sm border rounded hover:bg-gray-100 dark:hover:bg-dark-accent',
                    autoFitMode === 'screen'
                      ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                      : 'bg-white dark:bg-dark-card'
                  )}
                  aria-label={t('pdf_viewer.zoom.fit_to_screen')}
                >
                  <Scan size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t('pdf_viewer.zoom.fit_to_screen')}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>

        {/* Right side - Tools */}
        <div className="flex items-center gap-2">
          {/* Pan / Text / Search — all on one row */}
          <TooltipProvider>
            <div className="flex items-center gap-1 border-r pr-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={createBoardSafeHandler(() => { handleCloseSearch(); setInteractionMode('pan') })}
                    onPointerDown={stopBoardEvents}
                    className={cn(
                      'p-2 text-sm border rounded hover:bg-gray-100 dark:hover:bg-dark-accent',
                      interactionMode === 'pan' && !isSearchOpen
                        ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                        : 'bg-white dark:bg-dark-card'
                    )}
                    aria-label={t('pdf_viewer.tools.pan_mode')}
                  >
                    <Hand size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{t('pdf_viewer.tools.pan_mode')}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={ENABLE_TEXT_SELECTION ? createBoardSafeHandler(() => { handleCloseSearch(); setInteractionMode('text') }) : undefined}
                    onPointerDown={ENABLE_TEXT_SELECTION ? stopBoardEvents : undefined}
                    disabled={!ENABLE_TEXT_SELECTION}
                    className={cn(
                      'p-2 text-sm border rounded',
                      !ENABLE_TEXT_SELECTION
                        ? 'bg-gray-50 dark:bg-dark-muted text-gray-300 dark:text-gray-600 border-gray-200 dark:border-dark-border cursor-not-allowed'
                        : interactionMode === 'text' && !isSearchOpen
                          ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                          : 'bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent'
                    )}
                    aria-label={t('pdf_viewer.tools.text_select_mode')}
                  >
                    <Type size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{t('pdf_viewer.tools.text_select_mode')}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={ENABLE_DOCUMENT_SEARCH ? createBoardSafeHandler(() => {
                      if (isSearchOpen) {
                        handleCloseSearch()
                      } else {
                        setIsSearchOpen(true)
                        setInteractionMode('pan')
                        setTimeout(() => searchInputRef.current?.focus(), 0)
                      }
                    }) : undefined}
                    onPointerDown={ENABLE_DOCUMENT_SEARCH ? stopBoardEvents : undefined}
                    disabled={!ENABLE_DOCUMENT_SEARCH}
                    className={cn(
                      'p-2 text-sm border rounded',
                      !ENABLE_DOCUMENT_SEARCH
                        ? 'bg-gray-50 dark:bg-dark-muted text-gray-300 dark:text-gray-600 border-gray-200 dark:border-dark-border cursor-not-allowed'
                        : isSearchOpen
                          ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700'
                          : 'bg-white dark:bg-dark-card hover:bg-gray-100 dark:hover:bg-dark-accent'
                    )}
                    aria-label={String(t('pdf_viewer.tools.search'))}
                    title={String(t('pdf_viewer.tools.search'))}
                  >
                    <Search size={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{String(t('pdf_viewer.tools.search'))} (Ctrl+F / Cmd+F)</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {/* Rotate buttons */}
          <TooltipProvider>
            <div className="flex items-center gap-1 border-r pr-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={createBoardSafeHandler(rotateCounterClockwise)}
                    onPointerDown={stopBoardEvents}
                    className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
                    aria-label={t('pdf_viewer.tools.rotate_left')}
                  >
                    <img src="/images/icon-rotate-left.svg" alt={t('pdf_viewer.tools.rotate_left')} width={16} height={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{t('pdf_viewer.tools.rotate_left')}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={createBoardSafeHandler(rotateClockwise)}
                    onPointerDown={stopBoardEvents}
                    className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
                    aria-label={t('pdf_viewer.tools.rotate_right')}
                  >
                    <img src="/images/icon-rotate-right.svg" alt={t('pdf_viewer.tools.rotate_right')} width={16} height={16} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{t('pdf_viewer.tools.rotate_right')}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {/* Action buttons */}
          <button
            onClick={createBoardSafeHandler(printPdf)}
            onPointerDown={stopBoardEvents}
            className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
            title={t('pdf_viewer.tools.print')}
          >
            <Printer size={16} />
          </button>
          <button
            onClick={createBoardSafeHandler(downloadPdf)}
            onPointerDown={stopBoardEvents}
            className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
            title={t('pdf_viewer.tools.download')}
          >
            <Download size={16} />
          </button>
          <button
            onClick={createBoardSafeHandler(toggleFullscreen)}
            onPointerDown={stopBoardEvents}
            className="p-2 text-sm bg-white dark:bg-dark-card border rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
            title={isFullscreen ? t('pdf_viewer.tools.exit_fullscreen') : t('pdf_viewer.tools.enter_fullscreen')}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>}

      {/* Search in document bar — shown when search icon is active or Cmd+F */}
      {isSearchOpen && (
        <div
          className="flex items-center gap-2 p-2 border-b bg-white dark:bg-dark-card"
          onPointerDown={stopBoardEvents}
          onClick={stopBoardEvents}
        >
          <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                // Cancel debounce to avoid duplicate API call
                if (searchDebounceRef.current) {
                  clearTimeout(searchDebounceRef.current)
                  searchDebounceRef.current = null
                }
                const shift = e.shiftKey
                executeSearch(searchQuery).then(() => {
                  if (shift) handleFindPrevious()
                  else handleFindNext()
                })
              }
              if (e.key === 'Escape') handleCloseSearch()
            }}
            placeholder={t('pdf_viewer.tools.search_placeholder')}
            className="flex-1 min-w-0 px-2 py-1.5 text-sm border border-gray-200 dark:border-dark-border rounded bg-white dark:bg-dark-card focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={t('pdf_viewer.tools.search_placeholder')}
          />
          {searchQuery.trim() && (
            <span className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap shrink-0 flex items-center gap-1">
              {isSearching ? (
                <Loader2 className="w-3 h-3 animate-spin text-gray-400 dark:text-gray-500" />
              ) : totalMatchCount > 0 ? (
                `${globalMatchIndex} of ${totalMatchCount}`
              ) : (
                '0 results'
              )}
            </span>
          )}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={createBoardSafeHandler(handleFindPrevious)}
              onPointerDown={stopBoardEvents}
              className="p-1.5 text-sm bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-dark-muted"
              title={t('pdf_viewer.tools.find_previous')}
              aria-label={t('pdf_viewer.tools.find_previous')}
            >
              <ChevronUp size={16} />
            </button>
            <button
              type="button"
              onClick={createBoardSafeHandler(handleFindNext)}
              onPointerDown={stopBoardEvents}
              className="p-1.5 text-sm bg-gray-100 dark:bg-dark-accent border rounded hover:bg-gray-200 dark:hover:bg-dark-muted"
              title={t('pdf_viewer.tools.find_next')}
              aria-label={t('pdf_viewer.tools.find_next')}
            >
              <ChevronDown size={16} />
            </button>
            <button
              type="button"
              onClick={createBoardSafeHandler(handleCloseSearch)}
              onPointerDown={stopBoardEvents}
              className="p-1.5 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded"
              title={t('pdf_viewer.tools.close_search')}
              aria-label={t('pdf_viewer.tools.close_search')}
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Canvas + the FS-657 match list, side by side */}
      <div className="flex-1 min-h-0 flex">
      {/* PDF Canvas - with horizontal and vertical scrolling when zoomed, and drag-to-pan */}
      <div
        ref={setContainerRef}
        tabIndex={0}
        className="flex-1 min-w-0 bg-gray-100 dark:bg-dark-accent p-4 focus:outline-none"
        style={{
          overflow: 'auto',
          // FS-562: reserve the scrollbar gutter so the vertical scrollbar
          // appearing/disappearing during zoom doesn't change clientWidth. That
          // width drives the fit-to-width baseScale; if it jitters, the zoom %
          // drifts off the clean 10% grid (100 → 110 → 119 → 128 …).
          scrollbarGutter: 'stable',
          cursor: interactionMode === 'pan'
            ? (isDragging ? 'grabbing' : 'grab')
            : 'auto',
        }}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerLeave={handleDragEnd}
      >
        {/* FS-580: center the page like Chrome's viewer. A block wrapper sized to
            its content with auto inline margins centers when the page is narrower
            than the container, and — unlike flex justify-center — the margins
            resolve to 0 when the page is wider (zoomed in), so the left edge
            stays reachable by scroll/pan instead of being clipped. */}
        <div style={{ display: 'block', width: 'max-content', marginInline: 'auto', minHeight: 'min-content', position: 'relative' }}>
          <canvas
            ref={canvasRef}
            className="shadow-lg"
            style={{
              transition: 'opacity 0.15s ease-in-out',
              opacity: isRendering ? 0.7 : 1,
              pointerEvents: 'none', // Let container handle all pointer events for drag
              display: 'block',
            }}
          />
          <div
            ref={textLayerRef}
            className="textLayer"
            style={{
              pointerEvents: interactionMode === 'text' ? 'auto' : 'none',
              overflow: 'hidden',
              userSelect: interactionMode === 'text' ? 'text' : 'none',
            }}
            onMouseDown={() => {
              // Add 'selecting' class so pdfjs CSS constrains the highlight to the document
              textLayerRef.current?.classList.add('selecting')
            }}
            onMouseUp={() => {
              textLayerRef.current?.classList.remove('selecting')
            }}
          />
          {/* Position-based search highlight overlay — rendered from bounding boxes
              extracted during indexing (Docling/PyMuPDF stage).
              Uses PyMuPDF page dimensions (from page_highlights) for coordinate
              mapping to avoid mismatch with PDF.js viewport dimensions.
              Hidden while page is rendering to prevent flash with stale canvas dims. */}
          {(() => {
            // Wait for canvas render to complete so we have stable dimensions
            if (!isSearchOpen || isRendering || !renderedCanvasDims) return null
            const pageData = positionHighlights.get(currentPage - 1)
            if (!pageData || pageData.matches.length === 0) return null
            // Use PyMuPDF's page dimensions — same coordinate space as the bounding boxes
            const pdfW = pageData.page_width
            const pdfH = pageData.page_height
            if (!pdfW || !pdfH) return null
            const { w: canvasW, h: canvasH } = renderedCanvasDims
            if (!canvasW || !canvasH) return null
            const scaleX = canvasW / pdfW
            const scaleY = canvasH / pdfH
            return (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: canvasW,
                  height: canvasH,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                }}
              >
                {pageData.matches.map((match, matchIdx) =>
                  match.boxes.map((hl, boxIdx) => (
                  <div
                    key={`hl-${matchIdx}-${boxIdx}`}
                    style={{
                      position: 'absolute',
                      left: hl.x0 * scaleX,
                      top: hl.y0 * scaleY,
                      width: (hl.x1 - hl.x0) * scaleX,
                      height: (hl.y1 - hl.y0) * scaleY,
                      backgroundColor: matchIdx === activeHighlightIndex
                        ? 'rgba(255, 150, 0, 0.5)'
                        : 'rgba(255, 235, 0, 0.4)',
                      borderRadius: '2px',
                      transition: 'background-color 0.15s ease',
                    }}
                  />
                  ))
                )}
              </div>
            )
          })()}
        </div>
      </div>
      {isSearchOpen && searchQuery.trim() && (
        <PreviewMatchList
          matches={allMatches}
          activeIndex={globalMatchIndex - 1}
          query={searchQuery}
          onSelect={handleSelectMatch}
          isSearching={isSearching}
          className="w-[264px] shrink-0 border-l border-gray-200 dark:border-dark-border"
        />
      )}
      </div>
    </div>
  )
}))
